import React, { useState, useEffect } from 'react';
import { FileText, Download, ArrowLeft, Printer, User, CheckCircle } from 'lucide-react';
import { showGlobalNotification } from '../hooks/useNotifications';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface AssignmentFormData {
  id: number;
  form_number: string;
  form_type?: 'assignment' | 'return';
  mobile_phone_id?: number;
  handheld_terminal_id?: number;
  computer_id?: number;
  monitor_id?: number;
  equipment_assignment_id?: number;
  assigned_user_id: number;
  assigned_by_user_id: number;
  assignment_date: string;
  form_status: string;
  notes?: string;
  return_photos?: string[] | string | null;
  created_at: string;
  // Joined fields
  inventory_number: string;
  brand: string;
  model: string;
  // Mobile phone fields
  imei1?: string;
  imei2?: string;
  color?: string;
  phone_storage?: string;
  creation_date?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  contract_price?: number;
  monthly_payment?: number;
  // Computer fields
  serial_number?: string;
  processor?: string;
  ram?: string;
  graphics_card?: string;
  computer_storage?: string;
  storage_type?: string;
  purchase_date?: string;
  warranty_end_date?: string;
  // Monitor fields
  monitor_serial_number?: string;
  terminal_serial_number?: string;
  terminal_ip_address?: string;
  terminal_mac_address?: string;
  panel_type?: string;
  screen_size?: string;
  // User fields
  assigned_user_name: string;
  assigned_user_email: string;
  assigned_user_phone?: string;
  assigned_user_department?: string;
  assigned_user_title?: string;
  assigned_by_name: string;
  assigned_by_email: string;
  assigned_by_title?: string;
  company_name?: string;
  company_logo_url?: string | null;
  location_name?: string;
  delivered_equipment?: string[] | null;
  received_equipment?: string[] | null;
  form_items?: FormItem[] | null;
}

// Çok kalemli formların (ör. tüm zimmetlerin toplu iadesi) kalemleri
interface FormItem {
  type: 'computer' | 'mobile_phone' | 'monitor' | 'gsm_line' | 'equipment' | string;
  category?: string | null;
  brand?: string | null;
  model?: string | null;
  serial?: string | null;
  inventory_number?: string | null;
  quantity?: number;
}

const ITEM_TYPE_LABELS: Record<string, string> = {
  computer: 'Bilgisayar',
  mobile_phone: 'Cep Telefonu',
  monitor: 'Monitör',
  handheld_terminal: 'IoT Ekipmanı',
  gsm_line: 'GSM Hat',
  equipment: 'Ekipman'
};

// Eski toplu iade formları kalemleri yalnızca metin olarak saklıyordu: "Telefon: Samsung A54 (TR-MOB-1)"
const parseLegacyItem = (text: string): FormItem => {
  const match = text.match(/^([^:]+):\s*(.*?)(?:\s*\(([^)]*)\))?$/);
  if (!match) return { type: 'equipment', model: text, quantity: 1 };
  const [, label, rest, paren] = match;
  if (label === 'GSM Hat') return { type: 'gsm_line', serial: rest, quantity: 1 };
  const type = { Bilgisayar: 'computer', Telefon: 'mobile_phone', 'Monitör': 'monitor' }[label] || 'equipment';
  const qty = paren?.match(/^(\d+) adet$/);
  return {
    type,
    model: rest,
    inventory_number: qty ? null : paren || null,
    quantity: qty ? Number(qty[1]) : 1
  };
};

interface ZimmetTemplate {
  id: number;
  name: string;
  description?: string;
  template_key: string;
  file_path?: string;
  file_original_name?: string;
  is_active: boolean;
  is_default: boolean;
}

const AssignmentFormPDF: React.FC = () => {
  const [formData, setFormData] = useState<AssignmentFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [assignmentDefinitions, setAssignmentDefinitions] = useState<any[]>([]);
  const [deviceAssigned, setDeviceAssigned] = useState<boolean>(false);
  const [assigning, setAssigning] = useState<boolean>(false);
  const [deviceType, setDeviceType] = useState<'mobile' | 'computer' | 'monitor' | 'iot' | 'equipment' | null>(null);
  const [activeTemplate, setActiveTemplate] = useState<ZimmetTemplate | null>(null);

  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const id = pathParts[pathParts.length - 1];

    if (id && !isNaN(Number(id))) {
      Promise.all([loadFormData(id), loadActiveTemplate()]);
    } else {
      showGlobalNotification.error('Hata', 'Geçersiz form ID');
      setLoading(false);
    }
  }, []);

  // Cihaz tipi belirlendikten sonra tanımları yükle
  useEffect(() => {
    if (formData) {
      loadAssignmentDefinitions(formData);
    }
  }, [formData?.id]);

  const loadFormData = async (formId: string) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/assignment-forms/${formId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Form bulunamadı');
      }
      
      const data = await response.json();
      setFormData(data);
      
      // Cihaz tipini belirle ve atanma durumunu kontrol et
      if (data.mobile_phone_id) {
        setDeviceType('mobile');
        await checkDeviceAssignment(data.mobile_phone_id, 'mobile');
      } else if (data.handheld_terminal_id) {
        setDeviceType('iot');
        await checkDeviceAssignment(data.handheld_terminal_id, 'iot');
      } else if (data.computer_id) {
        setDeviceType('computer');
        await checkDeviceAssignment(data.computer_id, 'computer');
      } else if (data.monitor_id) {
        setDeviceType('monitor');
        setDeviceAssigned(true);
      } else if (data.equipment_assignment_id) {
        setDeviceType('equipment');
        setDeviceAssigned(true);
      } else if (data.form_items?.length || data.received_equipment?.length) {
        // Çok kalemli form: tek bir cihaza bağlı değil
        setDeviceAssigned(true);
      } else {
        showGlobalNotification.error('Hata', 'Geçersiz form: Cihaz bilgisi bulunamadı');
      }
    } catch (error: any) {
      showGlobalNotification.error('Hata', error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkDeviceAssignment = async (deviceId: number, type: 'mobile' | 'computer' | 'iot') => {
    try {
      const endpoint = type === 'mobile' ? `/api/mobile-phones/${deviceId}`
        : type === 'iot' ? `/api/handheld-terminals/${deviceId}`
        : `/api/computers/${deviceId}`;
      const deviceName = type === 'mobile' ? 'Telefon' : type === 'iot' ? 'El terminali' : 'Bilgisayar';
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const deviceData = await response.json();
        setDeviceAssigned(!!deviceData.assigned_user_id);
        console.log(`${deviceName} ID ${deviceId} atama durumu:`, deviceData.assigned_user_id ? 'Atanmış' : 'Atanmamış');
      } else if (response.status === 404) {
        // Cihaz bulunamadı, atanmamış olarak işaretle
        setDeviceAssigned(false);
        console.warn(`${deviceName} ID ${deviceId} bulunamadı - atanmamış olarak işaretlendi`);
        showGlobalNotification.warning('Uyarı', `${deviceName} ID ${deviceId} bulunamadı. Zimmetleme işlemi yapılamayabilir.`);
      } else {
        // Diğer HTTP hataları
        const errorData = await response.json().catch(() => ({}));
        console.error(`${deviceName} ID ${deviceId} kontrol hatası:`, response.status, errorData);
        setDeviceAssigned(false);
        showGlobalNotification.error('Hata', `${deviceName} bilgileri alınamadı: ${errorData.error || 'Bilinmeyen hata'}`);
      }
    } catch (error) {
      console.error('Cihaz atama durumu kontrol hatası:', error);
      // Hata durumunda atanmamış olarak işaretle
      setDeviceAssigned(false);
      showGlobalNotification.error('Hata', 'Cihaz bilgileri kontrol edilemedi');
    }
  };

  const loadActiveTemplate = async () => {
    try {
      const response = await fetch('/api/zimmet-templates/active', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActiveTemplate(data);
      }
    } catch (error) {
      console.error('Aktif şablon yükleme hatası:', error);
    }
  };

  const loadAssignmentDefinitions = async (data: AssignmentFormData) => {
    try {
      const categoriesResponse = await fetch('/api/assignment-categories/all', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!categoriesResponse.ok) return;

      const categoriesData = await categoriesResponse.json();
      const categories: any[] = categoriesData.data || [];

      // Cihaz tipine göre kategori adı eşleşmesi
      let matchCategory: any = null;
      if (data.mobile_phone_id) {
        matchCategory = categories.find((c: any) =>
          c.name.toLowerCase().includes('cep') || c.name.toLowerCase().includes('mobile')
        );
      } else if (data.handheld_terminal_id) {
        matchCategory = categories.find((c: any) =>
          c.name.toLowerCase().includes('iot') || c.name.toLowerCase().includes('terminal')
        );
      } else if (data.computer_id) {
        matchCategory = categories.find((c: any) =>
          c.name.toLowerCase().includes('bilgisayar') || c.name.toLowerCase().includes('computer') || c.name.toLowerCase().includes('laptop')
        );
      } else if (data.monitor_id) {
        matchCategory = categories.find((c: any) =>
          c.name.toLowerCase().includes('monitör') || c.name.toLowerCase().includes('monitor')
        );
      }

      // Eşleşen kategori yoksa ilk aktif kategoriyi kullan
      if (!matchCategory && categories.length > 0) {
        matchCategory = categories.find((c: any) => c.is_active) || categories[0];
      }

      if (matchCategory) {
        const defResponse = await fetch(`/api/assignment-definitions?category_id=${matchCategory.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });
        if (defResponse.ok) {
          const defData = await defResponse.json();
          setAssignmentDefinitions(defData.data || []);
        }
      }
    } catch (error) {
      console.error('Zimmet tanımları yükleme hatası:', error);
    }
  };

  const handlePrint = () => {
    // Use browser's native print function
    window.print();
  };

  const createTextBasedPDF = (data: AssignmentFormData) => {
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        putOnlyUsedFonts: true
      });

      // Set font - use times for better Turkish character support
      pdf.setFont('times');
      
      // Title
      pdf.setFontSize(16);
      pdf.setFont('times', 'bold');
      const deviceTitle = data.mobile_phone_id ? 'CEP TELEFONU' : data.handheld_terminal_id ? 'IOT EKIPMANI' : 'BILGISAYAR';
      pdf.text(`${deviceTitle} ZIMMET TESLIM FORMU`, 105, 20, { align: 'center' });
      
      // Form info
      pdf.setFontSize(10);
      pdf.setFont('times', 'normal');
      pdf.text(`Form No: ${data.form_number}`, 20, 30);
      pdf.text(`Tarih: ${new Date(data.assignment_date).toLocaleDateString('tr-TR')}`, 20, 35);
      
      // Device info section
      pdf.setFontSize(12);
      pdf.setFont('times', 'bold');
      pdf.text('CIHAZ BILGILERI', 20, 45);
      
      pdf.setFontSize(10);
      pdf.setFont('times', 'normal');
      let yPos = 55;
      
      pdf.text(`Envanter No: ${data.inventory_number}`, 20, yPos);
      yPos += 5;
      pdf.text(`Marka/Model: ${data.brand} ${data.model}`, 20, yPos);
      yPos += 5;
      
      // Mobile phone specific fields
      if (data.mobile_phone_id) {
        if (data.imei1) {
          pdf.text(`IMEI 1: ${data.imei1}`, 20, yPos);
          yPos += 5;
        }
        if (data.imei2) {
          pdf.text(`IMEI 2: ${data.imei2}`, 20, yPos);
          yPos += 5;
        }
        if (data.color) {
          pdf.text(`Renk: ${data.color}`, 20, yPos);
          yPos += 5;
        }
        if (data.phone_storage) {
          pdf.text(`Hafıza: ${data.phone_storage}`, 20, yPos);
          yPos += 5;
        }
      }
      
      // IoT (el terminali) specific fields
      if (data.handheld_terminal_id) {
        if (data.terminal_serial_number) {
          pdf.text(`Seri No: ${data.terminal_serial_number}`, 20, yPos);
          yPos += 5;
        }
        if (data.terminal_ip_address) {
          pdf.text(`IP: ${data.terminal_ip_address}`, 20, yPos);
          yPos += 5;
        }
        if (data.terminal_mac_address) {
          pdf.text(`MAC: ${data.terminal_mac_address}`, 20, yPos);
          yPos += 5;
        }
      }

      // Computer specific fields
      if (data.computer_id) {
        if (data.serial_number) {
          pdf.text(`Seri No: ${data.serial_number}`, 20, yPos);
          yPos += 5;
        }
        if (data.processor) {
          pdf.text(`Islemci: ${data.processor}`, 20, yPos);
          yPos += 5;
        }
        if (data.ram) {
          pdf.text(`RAM: ${data.ram}`, 20, yPos);
          yPos += 5;
        }
        if (data.graphics_card) {
          pdf.text(`Ekran Karti: ${data.graphics_card}`, 20, yPos);
          yPos += 5;
        }
        if (data.computer_storage) {
          pdf.text(`Depolama: ${data.computer_storage}`, 20, yPos);
          yPos += 5;
        }
        if (data.storage_type) {
          pdf.text(`Depolama Tipi: ${data.storage_type}`, 20, yPos);
          yPos += 5;
        }
      }
      
      // User info section
      yPos += 10;
      pdf.setFontSize(12);
      pdf.setFont('times', 'bold');
      pdf.text('KULLANICI BILGILERI', 20, yPos);
      yPos += 10;
      
      pdf.setFontSize(10);
      pdf.setFont('times', 'normal');
      pdf.text(`Ad Soyad: ${data.assigned_user_name}`, 20, yPos);
      yPos += 5;
      pdf.text(`E-posta: ${data.assigned_user_email}`, 20, yPos);
      yPos += 5;
      if (data.assigned_user_phone) {
        pdf.text(`Telefon: ${data.assigned_user_phone}`, 20, yPos);
        yPos += 5;
      }
      if (data.assigned_user_department) {
        pdf.text(`Departman: ${data.assigned_user_department}`, 20, yPos);
        yPos += 5;
      }
      
      // Terms section - only for assignment, not for return
      yPos += 10;
      let signatureY = yPos + 50;
      if (data.form_type !== 'return') {
        pdf.setFontSize(12);
        pdf.setFont('times', 'bold');
        pdf.text('KULLANIM KURALLARI VE SORUMLULUKLAR', 20, yPos);
        yPos += 10;

        pdf.setFontSize(9);
        pdf.setFont('times', 'normal');
        const terms = [
          '• Cihaz sadece is amacli kullanilacaktir.',
          '• Ariza, kayip, hasarlardan kullanici sorumludur.',
          '• Is iliskisi bitiminde cihaz iade edilecektir.',
          '• Cihazin guvenligi kullanicinin sorumluluğundadir.',
          '• Izinsiz yazilim yuklemesi yapilamaz.',
          '• Kisisel amacli kullanim yasaktir.'
        ];

        terms.forEach((term, index) => {
          pdf.text(term, 20, yPos + (index * 5));
        });

        signatureY = yPos + 40;
      } else {
        signatureY = yPos + 10;
      }

      // Signatures
      pdf.setFontSize(12);
      pdf.setFont('times', 'bold');
      pdf.text('TESLIM EDEN', 30, signatureY);
      pdf.text('TESLIM ALAN', 120, signatureY);
      
      pdf.setFontSize(10);
      pdf.setFont('times', 'normal');
      pdf.text(data.assigned_by_name, 30, 210);
      pdf.text('Imza/Tarih', 30, 215);
      pdf.text(data.assigned_user_name, 120, 210);
      pdf.text('Imza / Tarih', 120, 215);
      
      // Footer
      pdf.setFontSize(8);
      pdf.text('Bu form IT Toolbox sistemi tarafından otomatik olarak oluşturulmuştur.', 105, 280, { align: 'center' });
      
      return pdf;
    } catch (error) {
      console.error('Text-based PDF creation error:', error);
      return null;
    }
  };

  const handleAssignDevice = async () => {
    if (!formData || !deviceType || deviceType === 'equipment' || deviceType === 'monitor') return;

    try {
      setAssigning(true);

      const deviceId = deviceType === 'mobile' ? formData.mobile_phone_id
        : deviceType === 'iot' ? formData.handheld_terminal_id
        : formData.computer_id;
      const endpoint = deviceType === 'mobile'
        ? `/api/mobile-phones/${deviceId}/assign`
        : deviceType === 'iot'
        ? `/api/handheld-terminals/${deviceId}/quick-assign`
        : `/api/computers/${deviceId}/assign`;
      const deviceName = deviceType === 'mobile' ? 'Telefon' : deviceType === 'iot' ? 'El terminali' : 'Bilgisayar';
      const assignedUserId = parseInt(formData.assigned_user_id.toString());

      const response = await fetch(endpoint, {
        method: deviceType === 'iot' ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(deviceType === 'iot'
          ? { assigned_user_id: assignedUserId }
          : { user_id: assignedUserId })
      });

      if (response.ok) {
        setDeviceAssigned(true);
        showGlobalNotification.success('Başarılı', `${deviceName} başarıyla zimmetlendi`);
      } else {
        const errorData = await response.json();
        showGlobalNotification.error('Hata', errorData.error || errorData.message || 'Zimmetleme işlemi başarısız');
      }
    } catch (error) {
      console.error('Zimmetleme hatası:', error);
      showGlobalNotification.error('Hata', 'Zimmetleme işlemi sırasında bir hata oluştu');
    } finally {
      setAssigning(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const content = document.getElementById('assignment-form-content');
      if (!content) {
        showGlobalNotification.error('Hata', 'Form içeriği bulunamadı');
        return;
      }

      // Loading notification
      showGlobalNotification.info('Bilgi', 'PDF oluşturuluyor...');

      // Hide header controls during PDF generation
      const headerControls = document.querySelector('.print\\:hidden') as HTMLElement;
      const originalDisplay = headerControls?.style.display;
      if (headerControls) {
        headerControls.style.display = 'none';
      }

      // Create canvas from HTML content - capture only the form content
      const canvas = await html2canvas(content, {
        scale: 2, // High quality
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: content.offsetWidth,
        height: content.offsetHeight,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        removeContainer: true,
        foreignObjectRendering: false,
        imageTimeout: 0,
      });

      // Restore header controls
      if (headerControls && originalDisplay !== undefined) {
        headerControls.style.display = originalDisplay;
      }

      // A4 dimensions in mm (210 x 297)
      const pdfWidth = 210;
      const pdfHeight = 297;

      // Create PDF with compression settings
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        precision: 2
      });

      // Calculate dimensions to center content properly
      const canvasAspectRatio = canvas.width / canvas.height;
      const pdfAspectRatio = pdfWidth / pdfHeight;

      let imgWidth: number, imgHeight: number, xOffset: number, yOffset: number;

      if (canvasAspectRatio > pdfAspectRatio) {
        imgWidth = pdfWidth;
        imgHeight = pdfWidth / canvasAspectRatio;
        xOffset = 0;
        yOffset = (pdfHeight - imgHeight) / 2;
      } else {
        imgHeight = pdfHeight;
        imgWidth = pdfHeight * canvasAspectRatio;
        xOffset = (pdfWidth - imgWidth) / 2;
        yOffset = 0;
      }

      // Add first page (form content)
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight);

      // Add second page with return photos if this is a return form and has photos
      const photosSection = document.getElementById('return-photos-content');
      if (formData?.form_type === 'return' && photosSection) {
        const photosCanvas = await html2canvas(photosSection, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
        });

        pdf.addPage();

        const pAspect = photosCanvas.width / photosCanvas.height;
        let pImgW: number, pImgH: number, pX: number, pY: number;
        if (pAspect > pdfWidth / pdfHeight) {
          pImgW = pdfWidth;
          pImgH = pdfWidth / pAspect;
          pX = 0;
          pY = (pdfHeight - pImgH) / 2;
        } else {
          pImgH = pdfHeight;
          pImgW = pdfHeight * pAspect;
          pX = (pdfWidth - pImgW) / 2;
          pY = 0;
        }
        pdf.addImage(photosCanvas.toDataURL('image/png'), 'PNG', pX, pY, pImgW, pImgH);
      }

      // Download PDF
      pdf.save(`zimmet-formu-${formData?.form_number || 'unknown'}.pdf`);

      showGlobalNotification.success('Başarılı', 'PDF başarıyla indirildi');
    } catch (error) {
      console.error('PDF creation error:', error);
      showGlobalNotification.error('Hata', 'PDF oluşturulurken hata oluştu');
    }
  };

  // return_photos alanını güvenli şekilde dizi olarak döndür
  const getReturnPhotos = (): string[] => {
    if (!formData?.return_photos) return [];
    if (Array.isArray(formData.return_photos)) return formData.return_photos;
    try {
      const parsed = JSON.parse(formData.return_photos as string);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const goBack = () => {
    window.history.back();
  };

  // Cihaz tipini Türkçe döndür
  const getDeviceLabel = () => {
    if (!formData) return 'Cihaz';
    if (formData.mobile_phone_id) return 'Cep Telefonu';
    if (formData.handheld_terminal_id) return 'IoT Ekipmanı';
    if (formData.computer_id) return 'Bilgisayar';
    if (formData.monitor_id) return 'Monitör';
    return 'Cihaz';
  };

  // Cihaz seri / IMEI numarasını döndür
  const getDeviceSerial = () => {
    if (!formData) return '-';
    if (formData.mobile_phone_id) return formData.imei1 || '-';
    if (formData.handheld_terminal_id) return formData.terminal_serial_number || '-';
    if (formData.computer_id) return formData.serial_number || '-';
    if (formData.monitor_id) return formData.monitor_serial_number || '-';
    return '-';
  };

  // Formdaki kalemler: çok kalemli formlarda saklanan liste, eski toplu iade formlarında metin listesi,
  // tek cihazlı formlarda bağlı cihaz
  const isMultiItemForm = !!formData && !formData.mobile_phone_id && !formData.handheld_terminal_id && !formData.computer_id && !formData.monitor_id
    && !formData.equipment_assignment_id && !!(formData.form_items?.length || formData.received_equipment?.length);

  const getFormItems = (): FormItem[] => {
    if (!formData) return [];
    if (formData.form_items?.length) return formData.form_items;
    if (isMultiItemForm) return (formData.received_equipment || []).map(parseLegacyItem);
    return [{
      type: '', brand: formData.brand, model: formData.model, serial: getDeviceSerial(),
      inventory_number: formData.inventory_number, quantity: 1
    }];
  };

  const getItemTypeLabel = (item: FormItem) =>
    item.type ? (item.type === 'equipment' && item.category) || ITEM_TYPE_LABELS[item.type] || item.type : getDeviceLabel();

  // Kurumsal şablon form içeriği
  const renderCorporateFormContent = () => {
    if (!formData) return null;
    const isReturn = formData.form_type === 'return';
    const items = getFormItems();
    const taahhutMetni = assignmentDefinitions
      .filter(d => d.is_active !== false)
      .map(d => d.content)
      .join(' ');

    return (
      <>
        {/* Başlık */}
        <div style={{ borderBottom: '2px solid #000', paddingBottom: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '14pt', fontWeight: 'bold', letterSpacing: '1px' }}>
              {isReturn ? 'ZİMMET / GERİ TESLİM – TUTANAĞI' : 'ZİMMET / TESLİM – FORMU'}
            </div>
            <div style={{ fontSize: '9pt', marginTop: '4px' }}>
              Form No: <strong>{formData.form_number}</strong> &nbsp;|&nbsp;
              Tarih: <strong>{new Date(formData.assignment_date).toLocaleDateString('tr-TR')}</strong>
            </div>
          </div>
          {formData.company_logo_url && (
            <div style={{ flexShrink: 0, marginLeft: '12px' }}>
              <img
                src={formData.company_logo_url}
                alt={formData.company_name || 'Logo'}
                style={{ maxHeight: '50px', maxWidth: '120px', objectFit: 'contain' }}
                crossOrigin="anonymous"
              />
            </div>
          )}
        </div>

        {/* Kullanıcı Bilgileri Tablosu */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px', fontSize: '10pt' }}>
          <tbody>
            {[
              ['Şirket', formData.company_name || '-'],
              ['Ad Soyad', formData.assigned_user_name],
              ['Departman', formData.assigned_user_department || '-'],
              ['Ünvan', formData.assigned_user_title || '-'],
              ['Lokasyon', formData.location_name || formData.company_name || '-'],
            ].map(([label, value]) => (
              <tr key={label}>
                <td style={{ border: '1px solid #ccc', padding: '5px 8px', width: '28%', backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>{label}:</td>
                <td style={{ border: '1px solid #ccc', padding: '5px 8px' }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Ekipman Tablosu */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '10pt', backgroundColor: '#333', color: '#fff', padding: '5px 8px', marginBottom: '0' }}>
            {isReturn ? 'İADE EDİLEN EKİPMANLAR' : 'TESLİM EDİLEN EKİPMANLAR'}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                {['Sıra No', 'Ekipman', 'Marka / Model', 'Seri No / IMEI', 'Adet', 'Asset ID'].map(h => (
                  <th key={h} style={{ border: '1px solid #ccc', padding: '5px 6px', textAlign: 'center', fontWeight: 'bold' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index}>
                  <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{index + 1}</td>
                  <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{getItemTypeLabel(item)}</td>
                  <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{[item.brand, item.model].filter(Boolean).join(' ') || '-'}</td>
                  <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{item.serial || '-'}</td>
                  <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{item.quantity || 1}</td>
                  <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{item.inventory_number || '-'}</td>
                </tr>
              ))}
              {/* Boş satırlar: elle eklenecek kalemler için en az 3 satır */}
              {Array.from({ length: Math.max(0, 3 - items.length) }, (_, i) => i).map(i => (
                <tr key={i}>
                  {[...Array(6)].map((_, j) => (
                    <td key={j} style={{ border: '1px solid #ccc', padding: '12px 6px' }}>&nbsp;</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Teslim Edilen / Alınan Ekipmanlar */}
        {!isMultiItemForm && (formData.delivered_equipment?.length || formData.received_equipment?.length) ? (
          <div style={{ marginBottom: '14px', fontSize: '10pt', border: '1px solid #ccc', padding: '8px 12px', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
            {formData.delivered_equipment && formData.delivered_equipment.length > 0 && (
              <div style={{ marginBottom: formData.received_equipment?.length ? '6px' : '0' }}>
                <strong>Teslim Edilen Ekipmanlar:</strong>{' '}
                {formData.delivered_equipment.join(' • ')}
              </div>
            )}
            {formData.received_equipment && formData.received_equipment.length > 0 && (
              <div>
                <strong>Teslim Alınan Ekipmanlar:</strong>{' '}
                {formData.received_equipment.join(' • ')}
              </div>
            )}
          </div>
        ) : null}

        {/* Özel Notlar */}
        {formData.notes && (
          <div style={{ marginBottom: '14px', fontSize: '10pt' }}>
            <strong>Notlar:</strong> {formData.notes}
          </div>
        )}

        {/* Taahhüt Metni */}
        {!isReturn && (
          <div style={{ marginBottom: '20px', fontSize: '9.5pt', lineHeight: '1.6', border: '1px solid #ccc', padding: '10px', backgroundColor: '#fafafa' }}>
            {taahhutMetni
              ? taahhutMetni
              : 'Teslim aldığım, yukarıda belirtilen tüm cihaz ve aksesuarların şirket envanterine ait olduğunu; bu ekipmanları yalnızca iş amaçlı, özenli ve bilgi güvenliği kurallarına uygun şekilde kullanacağımı; kayıp, hasar veya arıza durumlarında durumu derhal IT birimine bildireceğimi; işten ayrılmam, görev değişikliğim veya talep edilmesi halinde ekipmanları eksiksiz şekilde iade edeceğimi ve kullanımımdan kaynaklanan her türlü hasar ve zarardan sorumlu olduğumu kabul ve taahhüt ederim.'}
          </div>
        )}

        {/* İmzalar */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt', marginTop: '10px' }}>
          <tbody>
            <tr>
              <td style={{ width: '48%', border: '1px solid #ccc', padding: '10px', verticalAlign: 'top' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' }}>
                  {isReturn ? 'GERİ TESLİM EDEN' : 'TESLİM EDEN'}
                </div>
                <div style={{ marginBottom: '10px' }}>Adı Soyad: <span style={{ borderBottom: '1px solid #999', display: 'inline-block', minWidth: '120px' }}>{isReturn ? formData.assigned_user_name : formData.assigned_by_name}</span></div>
                <div style={{ marginBottom: '10px' }}>Unvan: <span style={{ borderBottom: '1px solid #999', display: 'inline-block', minWidth: '130px' }}>{isReturn ? (formData.assigned_user_title || formData.assigned_user_department || '') : (formData.assigned_by_title || '')}</span></div>
                <div style={{ marginBottom: '10px' }}>Tarih: <span style={{ borderBottom: '1px solid #999', display: 'inline-block', minWidth: '130px' }}></span></div>
                <div style={{ marginTop: '20px' }}>İmza: <span style={{ borderBottom: '1px solid #999', display: 'inline-block', minWidth: '130px' }}></span></div>
              </td>
              <td style={{ width: '4%' }}></td>
              <td style={{ width: '48%', border: '1px solid #ccc', padding: '10px', verticalAlign: 'top' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' }}>
                  {isReturn ? 'TESLİM ALAN' : 'TESLİM ALAN'}
                </div>
                <div style={{ marginBottom: '10px' }}>Adı Soyad: <span style={{ borderBottom: '1px solid #999', display: 'inline-block', minWidth: '120px' }}>{isReturn ? formData.assigned_by_name : formData.assigned_user_name}</span></div>
                <div style={{ marginBottom: '10px' }}>Unvan: <span style={{ borderBottom: '1px solid #999', display: 'inline-block', minWidth: '130px' }}>{isReturn ? (formData.assigned_by_title || '') : (formData.assigned_user_title || formData.assigned_user_department || '')}</span></div>
                <div style={{ marginBottom: '10px' }}>Tarih: <span style={{ borderBottom: '1px solid #999', display: 'inline-block', minWidth: '130px' }}></span></div>
                <div style={{ marginTop: '20px' }}>İmza: <span style={{ borderBottom: '1px solid #999', display: 'inline-block', minWidth: '130px' }}></span></div>
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Form bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500">
            Talep ettiğiniz zimmet formu bulunamadı.
          </p>
          <button
            onClick={goBack}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Print Controls - Only visible on screen */}
      <div className="print:hidden bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={goBack}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Geri Dön
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {formData.form_type === 'return' ? 'Zimmet Geri Teslim Tutanağı' : 'Zimmet Teslim Formu'} - {formData.form_number}
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {formData.form_type === 'assignment' && !deviceAssigned && (
              <button
                onClick={handleAssignDevice}
                disabled={assigning}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <User className="w-4 h-4 mr-1" />
                {assigning ? 'Zimmetleniyor...' : 'Zimmetle'}
              </button>
            )}
            {formData.form_type === 'assignment' && deviceAssigned && (
              <div className="flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-md">
                <CheckCircle className="w-4 h-4 mr-1" />
                Zimmetlendi
              </div>
            )}
            {formData.form_type === 'return' && (
              <div className="flex items-center px-4 py-2 bg-primary/20 text-primary-dark rounded-md">
                <CheckCircle className="w-4 h-4 mr-1" />
                Zimmet Kaldırıldı
              </div>
            )}
            <button
              onClick={handlePrint}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              <Printer className="w-4 h-4 mr-1" />
              Yazdır
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-1" />
              PDF İndir
            </button>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="print:p-0 p-8">
        <div
          id="assignment-form-content"
          className="bg-white mx-auto print:shadow-none shadow-lg"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '20mm',
            fontSize: '11pt',
            lineHeight: '1.4',
            fontFamily: 'Arial, sans-serif',
            boxSizing: 'border-box' as const
          }}
        >
          {activeTemplate?.template_key === 'corporate' ? renderCorporateFormContent() : (
          <>
          {/* Header */}
          <div className="mb-3 border-b-2 border-gray-800 pb-2 flex items-center justify-between">
            <div className="flex-1 text-center">
              <h1 className="text-base font-bold uppercase tracking-wide">
                {formData.form_type === 'return' ? 'ZİMMET GERİ TESLİM TUTANAĞI' : 'ZİMMET TESLİM FORMU'}
              </h1>
              <p className="text-xs mt-1">
                Form No: <span className="font-bold">{formData.form_number}</span> |
                Tarih: {new Date(formData.assignment_date).toLocaleDateString('tr-TR')}
              </p>
            </div>
            {formData.company_logo_url && (
              <div className="flex-shrink-0 ml-3">
                <img
                  src={formData.company_logo_url}
                  alt={formData.company_name || 'Logo'}
                  style={{ maxHeight: '50px', maxWidth: '120px', objectFit: 'contain' }}
                  crossOrigin="anonymous"
                />
              </div>
            )}
          </div>

          {/* Company Info */}
          <div className="mb-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <strong>Şirket:</strong> {formData.company_name || 'Bilinmiyor'}
              </div>
              <div>
                <strong>Departman:</strong> {formData.assigned_user_department || 'Bilgi İşlem'}
              </div>
            </div>
          </div>

          {/* Device Information */}
          {isMultiItemForm ? (
          <div className="mb-4">
            <h3 className="text-sm font-bold mb-2 bg-gray-100 px-3 py-2 rounded">
              {formData.form_type === 'return' ? 'İADE EDİLEN CİHAZLAR' : 'CİHAZ BİLGİLERİ'}
            </h3>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  {['#', 'Tür', 'Marka / Model', 'Seri No / IMEI', 'Adet', 'Envanter No'].map(h => (
                    <th key={h} className="border border-gray-300 px-2 py-1 text-left font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getFormItems().map((item, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 px-2 py-1">{index + 1}</td>
                    <td className="border border-gray-300 px-2 py-1">{getItemTypeLabel(item)}</td>
                    <td className="border border-gray-300 px-2 py-1">{[item.brand, item.model].filter(Boolean).join(' ') || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1">{item.serial || '-'}</td>
                    <td className="border border-gray-300 px-2 py-1">{item.quantity || 1}</td>
                    <td className="border border-gray-300 px-2 py-1">{item.inventory_number || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ) : (
          <div className="mb-4">
            <h3 className="text-sm font-bold mb-2 bg-gray-100 px-3 py-2 rounded">
              CİHAZ BİLGİLERİ
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-1">
                <div>
                  <strong>Envanter No:</strong> {formData.inventory_number}
                </div>
                <div>
                  <strong>Marka/Model:</strong> {formData.brand} {formData.model}
                </div>
                {/* Mobile Phone Specific Fields */}
                {formData.mobile_phone_id && formData.imei1 && (
                  <div>
                    <strong>IMEI 1:</strong> {formData.imei1}
                  </div>
                )}
                {formData.mobile_phone_id && formData.imei2 && (
                  <div>
                    <strong>IMEI 2:</strong> {formData.imei2}
                  </div>
                )}
                {/* IoT (El Terminali) Specific Fields */}
                {formData.handheld_terminal_id && formData.terminal_serial_number && (
                  <div>
                    <strong>Seri No:</strong> {formData.terminal_serial_number}
                  </div>
                )}
                {formData.handheld_terminal_id && formData.terminal_ip_address && (
                  <div>
                    <strong>IP:</strong> {formData.terminal_ip_address}
                  </div>
                )}
                {/* Computer Specific Fields */}
                {formData.computer_id && formData.serial_number && (
                  <div>
                    <strong>Seri No:</strong> {formData.serial_number}
                  </div>
                )}
                {formData.computer_id && formData.processor && (
                  <div>
                    <strong>İşlemci:</strong> {formData.processor}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                {formData.handheld_terminal_id && formData.terminal_mac_address && (
                  <div>
                    <strong>MAC:</strong> {formData.terminal_mac_address}
                  </div>
                )}
                {/* Mobile Phone Specific Fields */}
                {formData.mobile_phone_id && formData.color && (
                  <div>
                    <strong>Renk:</strong> {formData.color}
                  </div>
                )}
                {formData.mobile_phone_id && formData.phone_storage && (
                  <div>
                    <strong>Hafıza:</strong> {formData.phone_storage}
                  </div>
                )}
                {formData.mobile_phone_id && formData.creation_date && (
                  <div>
                    <strong>Satın Alma Tarihi:</strong> {new Date(formData.creation_date).toLocaleDateString('tr-TR')}
                  </div>
                )}
                {formData.mobile_phone_id && formData.contract_start_date && (
                  <div>
                    <strong>Sözleşme Başlangıç:</strong> {new Date(formData.contract_start_date).toLocaleDateString('tr-TR')}
                  </div>
                )}
                {/* Computer Specific Fields */}
                {formData.computer_id && formData.ram && (
                  <div>
                    <strong>RAM:</strong> {formData.ram}
                  </div>
                )}
                {formData.computer_id && formData.graphics_card && (
                  <div>
                    <strong>Ekran Kartı:</strong> {formData.graphics_card}
                  </div>
                )}
                {formData.computer_id && formData.computer_storage && (
                  <div>
                    <strong>Depolama:</strong> {formData.computer_storage} {formData.storage_type && `(${formData.storage_type})`}
                  </div>
                )}
                {formData.computer_id && formData.purchase_date && (
                  <div>
                    <strong>Satın Alma Tarihi:</strong> {new Date(formData.purchase_date).toLocaleDateString('tr-TR')}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {/* User Information */}
          <div className="mb-4">
            <h3 className="text-sm font-bold mb-2 bg-gray-100 px-3 py-2 rounded">
              KULLANICI BİLGİLERİ
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-1">
                <div>
                  <strong>Ad Soyad:</strong> {formData.assigned_user_name}
                </div>
                <div>
                  <strong>E-posta:</strong> {formData.assigned_user_email}
                </div>
              </div>
              <div className="space-y-1">
                {formData.assigned_user_phone && (
                  <div>
                    <strong>Telefon:</strong> {formData.assigned_user_phone}
                  </div>
                )}
                {formData.assigned_user_department && (
                  <div>
                    <strong>Departman:</strong> {formData.assigned_user_department}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Terms and Conditions - Only for assignment, not for return */}
          {formData.form_type !== 'return' && (
            <div className="mb-4">
              <h3 className="text-sm font-bold mb-2 bg-gray-100 px-3 py-2 rounded">
                KULLANIM KURALLARI VE SORUMLULUKLAR
              </h3>
              <div className="text-sm grid grid-cols-2 gap-3">
                {assignmentDefinitions.length > 0 ? (
                  assignmentDefinitions.map((definition, index) => (
                    <p key={index} className="text-xs">• {definition.content}</p>
                  ))
                ) : (
                  <>
                    <p>• Cihaz sadece iş amaçlı kullanılacaktır.</p>
                    <p>• Cihazın güvenliği kullanıcının sorumluluğundadır.</p>
                    <p>• Arıza, kayıp, hasarlardan kullanıcı sorumludur.</p>
                    <p>• İzinsiz yazılım yüklemesi yapılamaz.</p>
                    <p>• İş ilişkisi bitiminde cihaz iade edilecektir.</p>
                    <p>• Kişisel amaçlı kullanım yasaktır.</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Accessories */}
          <div className="mb-4">
            <h3 className="text-sm font-bold mb-2 bg-gray-100 px-3 py-2 rounded">
              TESLİM EDİLEN AKSESUARLAR
            </h3>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="space-y-1">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-1 w-3 h-3" /> Şarj Aleti
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-1 w-3 h-3" /> Kulaklık
                </label>
              </div>
              <div className="space-y-1">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-1 w-3 h-3" /> USB Kablosu
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-1 w-3 h-3" /> Koruyucu Kılıf
                </label>
              </div>
              <div className="space-y-1">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-1 w-3 h-3" /> Ekran Koruyucu
                </label>
                <label className="flex items-center">
                  <input type="checkbox" className="mr-1 w-3 h-3" /> Diğer: _______
                </label>
              </div>
            </div>
          </div>

          {/* Notes */}
          {formData.notes && (
            <div className="mb-4">
              <h3 className="text-sm font-bold mb-2 bg-gray-100 px-3 py-2 rounded">
                ÖZEL NOTLAR
              </h3>
              <div className="text-sm border border-gray-300 p-3 rounded min-h-[20px]">
                {formData.notes}
              </div>
            </div>
          )}

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-6 mt-6">
            <div className="text-center">
              <div className="border-t-2 border-gray-800 pt-2 text-sm">
                <strong>{formData.form_type === 'return' ? 'GERİ TESLİM EDEN' : 'TESLİM EDEN'}</strong>
                <p className="mt-4 h-12 text-lg">{formData.form_type === 'return' ? formData.assigned_user_name : formData.assigned_by_name}</p>
                <p className="text-sm text-gray-600">İmza / Tarih</p>
              </div>
            </div>
            <div className="text-center">
              <div className="border-t-2 border-gray-800 pt-2 text-sm">
                <strong>{formData.form_type === 'return' ? 'TESLİM ALAN' : 'TESLİM ALAN'}</strong>
                <p className="mt-4 h-12 text-lg">{formData.form_type === 'return' ? formData.assigned_by_name : formData.assigned_user_name}</p>
                <p className="text-sm text-gray-600">İmza / Tarih</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 text-center text-sm text-gray-500 border-t border-gray-200 pt-2">
            Bu form IT Toolbox sistemi tarafından otomatik olarak oluşturulmuştur.
          </div>
          </>
          )}
        </div>
      </div>

      {/* Return Photos - Second Page (visible on screen) */}
      {formData.form_type === 'return' && getReturnPhotos().length > 0 && (
        <div id="return-photos-content" className="max-w-2xl mx-auto mt-6 mb-8 bg-white shadow-lg rounded-lg p-6 border border-gray-200">
          <h2 className="text-lg font-bold text-center text-gray-800 mb-1">ÜRÜN GÖRSELLERİ</h2>
          <p className="text-center text-sm text-gray-500 mb-4">{formData.form_number} — {formData.inventory_number} {formData.brand} {formData.model}</p>
          <div className="grid grid-cols-2 gap-4">
            {getReturnPhotos().map((photo, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                <p className="text-xs text-gray-500 px-2 py-1 bg-gray-50">Fotoğraf {idx + 1}</p>
                <img src={photo} alt={`Fotoğraf ${idx + 1}`} className="w-full object-contain max-h-64" crossOrigin="anonymous" />
              </div>
            ))}
          </div>
          <p className="mt-4 text-center text-xs text-gray-400">Bu form IT Toolbox sistemi tarafından otomatik olarak oluşturulmuştur.</p>
        </div>
      )}
      
      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          /* Hide everything except form content */
          body * {
            visibility: hidden;
          }
          
          #assignment-form-content,
          #assignment-form-content * {
            visibility: visible;
          }
          
          #assignment-form-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 20mm !important;
            background: white !important;
            color: black !important;
            font-size: 11pt !important;
            line-height: 1.4 !important;
            box-sizing: border-box !important;
          }

          @page {
            size: A4 portrait;
            margin: 0;
          }
        }
      `}} />
    </div>
  );
};

export default AssignmentFormPDF;
